import './style.css'
import { FilesetResolver, HandLandmarker } from '@mediapipe/tasks-vision'
import { distance, evaluateCircle, isFist, isOpenPalm, isPointingHand, makeCirclePoints, smoothPoint } from './gesture.js'
import { analyzeWholeStroke, SIGIL_SPELLS, classifySigil, makeOneStrokeFixture, makeSigilFixture } from './sigil.js'
import { getSpellAudioSpec } from './spell-audio.js'
import { clearTraceRecords, createTraceRecord, readTraceRecords, saveTraceRecord } from './trace-store.js'

const BUILD_ID = 'V0.7.4 · FIST RECOVERY'
const STROKE_CONTINUE_GRACE_MS = 1200
const RESULT_CARD_VISIBLE_MS = 4000

const ELEMENTS = {
  wind: {
    key: 'wind', rune: '◎', name: '風切', kicker: 'SPIRAL · WIND AWAKENED',
    copy: '螺旋喚起空氣旋流，風切沿外圈甦醒。<br>清亮，但鋒利。',
    color: '#9cf7cf', bright: '#ecfff6', deep: '#33c982', soft: 'rgba(51,201,130,.22)',
    colors: ['#ecfff6', '#bfffe1', '#73edb4', '#43dc99'], tones: [523, 659, 988],
  },
  fire: {
    key: 'fire', rune: '△', name: '點燃', kicker: 'TRIANGLE · FIRE AWAKENED',
    copy: '三角符號點燃圓心，火焰向上爆發。<br>熾熱，留下餘燼。',
    color: '#ff9a52', bright: '#fff2bd', deep: '#ff4f32', soft: 'rgba(255,79,50,.22)',
    colors: ['#fff2bd', '#ffba52', '#ff6938', '#ff3f2e'], tones: [196, 392, 784],
  },
  water: {
    key: 'water', rune: '≈', name: '潮汐', kicker: 'WAVE · WATER AWAKENED',
    copy: '水平波形推開水光，潮汐由圓心擴散。<br>柔軟，也有重量。',
    color: '#72d8ff', bright: '#e7fbff', deep: '#297cff', soft: 'rgba(41,124,255,.22)',
    colors: ['#e7fbff', '#8ee8ff', '#4cbcff', '#326fff'], tones: [261, 392, 523],
  },
  earth: {
    key: 'earth', rune: '□', name: '岩裂', kicker: 'SQUARE · EARTH AWAKENED',
    copy: '方形符號壓入地脈，岩石碎裂成形。<br>厚重，穩定，不退讓。',
    color: '#d7b879', bright: '#fff0bd', deep: '#8f6b3e', soft: 'rgba(176,132,72,.22)',
    colors: ['#fff0bd', '#d7b879', '#aa7f49', '#765438'], tones: [131, 196, 262],
  },
  love: {
    key: 'love', rune: '♥', name: '心光術', kicker: 'A SECRET SIGIL AWAKENED',
    copy: '玫瑰金心光在圓心綻放。<br>給最重要的人',
    color: '#f3a6b8', bright: '#fff1e9', deep: '#c85f7c', soft: 'rgba(243,166,184,.24)',
    colors: ['#fff1e9', '#f6bac6', '#e98ba4', '#c85f7c'], tones: [196, 392, 784],
  },
}

const $ = (selector) => document.querySelector(selector)
const app = $('#app')
const video = $('#camera')
const canvas = $('#magic-canvas')
const ctx = canvas.getContext('2d')
const intro = $('#intro')
const playUi = $('#play-ui')
const resultCard = $('#result')
const errorCard = $('#camera-error')
const errorCopy = $('#error-copy')
const statusPill = $('#status-pill')
const instructionKicker = $('#instruction-kicker')
const instructionMain = $('#instruction-main')
const instructionSub = $('#instruction-sub')
const castSteps = $('#cast-steps')
const gestureCue = $('#gesture-cue')
const gestureCueIcon = $('#gesture-cue-icon')
const gestureCueCopy = $('#gesture-cue-copy')
const spellProgress = $('#spell-progress')
const strokeFeedback = $('#stroke-feedback')
const sigilGuide = $('#sigil-guide')
const debugPanel = $('#debug-panel')
const buildVersion = $('#build-version')
const traceTools = $('.trace-tools')
const exportTracesButton = $('#export-traces')
const clearTracesButton = $('#clear-traces')
const resultRune = $('#result-rune')
const resultKicker = $('#result-kicker')
const resultName = $('#result-name')
const resultCopy = $('#result-copy')
const debug = new URLSearchParams(location.search).has('debug')

const state = {
  phase: 'intro',
  mode: 'camera',
  handLandmarker: null,
  modelReady: false,
  stream: null,
  stroke: [],
  drawing: false,
  gestureStage: 'awaitingFive',
  fiveFrames: 0,
  armedAt: 0,
  activeFrames: 0,
  inactiveFrames: 0,
  smoothedTip: null,
  landmarks: null,
  lastVideoTime: -1,
  lastInferenceAt: 0,
  lastEvaluation: null,
  spell: null,
  particles: [],
  castCount: 0,
  cooldownUntil: 0,
  messageTimer: null,
  animationStarted: false,
  audio: null,
  selectedElement: null,
  outerStroke: [],
  bridgeStroke: [],
  centralStroke: [],
  outerEvaluation: null,
  wholeAnalysis: null,
  beautify: null,
  beautifyTimer: null,
  resultRevealTimer: null,
  resultDismissTimer: null,
  strokePauseTimer: null,
  strokePausedAt: null,
  completionFistFrames: 0,
  completionOpenFrames: 0,
  castGestureStartedAt: null,
  strokeKind: 'whole',
  sigilResult: null,
  stableShape: null,
  stableFrames: 0,
  bridgeEntered: false,
  heartCandidateStroke: [],
  landmarkLostAt: null,
  totalStrokePoints: 0,
  wasmMimeFetchInstalled: false,
  muted: false,
  lastAudioSchedule: null,
  lastAudioError: null,
  attemptRecorded: false,
}

function readLocalTraces() {
  try { return readTraceRecords(window.localStorage) } catch { return [] }
}

function updateTraceTools() {
  const count = readLocalTraces().length
  exportTracesButton.textContent = `匯出軌跡 (${count})`
  exportTracesButton.disabled = count === 0
  clearTracesButton.disabled = count === 0
}

function recordTraceAttempt(outcome, reason = null) {
  if (state.attemptRecorded || state.totalStrokePoints < 2) return
  state.attemptRecorded = true
  const outer = state.outerStroke.length
    ? state.outerStroke
    : state.strokeKind === 'outer' ? state.stroke : []
  const bridge = state.bridgeStroke.length
    ? state.bridgeStroke
    : state.strokeKind === 'bridge' ? state.stroke : []
  const central = state.centralStroke.length
    ? state.centralStroke
    : state.strokeKind === 'sigil' ? state.stroke : []
  const record = createTraceRecord({
    build: BUILD_ID,
    mode: state.mode,
    outcome,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    raw: state.stroke,
    outer,
    bridge,
    central,
    evaluation: state.outerEvaluation || state.lastEvaluation,
    classification: state.sigilResult,
    reason,
  })
  try {
    saveTraceRecord(window.localStorage, record)
    updateTraceTools()
  } catch (error) {
    console.warn('Local trace storage unavailable', error)
  }
}

async function exportLocalTraces() {
  const traces = readLocalTraces()
  if (!traces.length) return
  const packet = {
    schemaVersion: 1,
    privacy: 'local-only-export',
    exportedAt: new Date().toISOString(),
    build: BUILD_ID,
    traces,
  }
  const filename = `magic-circle-traces-${new Date().toISOString().slice(0, 10)}.json`
  const json = JSON.stringify(packet, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const file = typeof File === 'function' ? new File([blob], filename, { type: 'application/json' }) : null
  if (file && typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({
        title: 'Magic Circle 軌跡資料',
        text: '本機校準軌跡 JSON（不含相機影像）',
        files: [file],
      })
      return
    } catch (error) {
      if (error?.name === 'AbortError') return
      console.warn('Native trace share unavailable; falling back to download', error)
    }
  }
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

function applyElement(key) {
  state.selectedElement = key
  const element = ELEMENTS[key]
  app.dataset.element = key
  app.style.setProperty('--element', element.color)
  app.style.setProperty('--element-soft', element.soft)
}

function setPhase(phase) {
  state.phase = phase
  app.dataset.phase = phase
}

function setStatus(text, kind = 'idle') {
  statusPill.className = `status-pill ${kind}`
  statusPill.querySelector('span').textContent = text
}

function setInstruction(kicker, main, sub = '') {
  instructionKicker.textContent = kicker
  instructionMain.textContent = main
  instructionSub.textContent = sub
}

function setCastStep(step) {
  castSteps.dataset.active = step
  const order = ['five', 'outer', 'sigil']
  castSteps.querySelectorAll('[data-step]').forEach((item) => {
    const current = order.indexOf(step)
    const index = order.indexOf(item.dataset.step)
    item.classList.toggle('active', index === current)
    item.classList.toggle('done', index < current)
  })
}

function setGestureCue(kind = 'hidden', copy = '') {
  gestureCue.className = `gesture-cue ${kind}`
  gestureCueIcon.textContent = kind === 'five' || kind === 'open' ? '✋' : kind === 'draw' ? '☝' : kind === 'fist' ? '✊' : ''
  gestureCueCopy.textContent = copy
}

function setStrokeFeedback(text, kind = 'idle') {
  strokeFeedback.className = `stroke-feedback ${kind}`
  strokeFeedback.querySelector('span').textContent = text
}

function resizeCanvas() {
  const ratio = Math.min(window.devicePixelRatio || 1, 2)
  const width = window.innerWidth
  const height = window.innerHeight
  if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
  }
}

function mapLandmark(point) {
  const width = window.innerWidth
  const height = window.innerHeight
  const sourceWidth = video.videoWidth || 640
  const sourceHeight = video.videoHeight || 480
  const sourceAspect = sourceWidth / sourceHeight
  const viewAspect = width / height
  let renderedWidth; let renderedHeight; let offsetX = 0; let offsetY = 0
  if (sourceAspect > viewAspect) {
    renderedHeight = height
    renderedWidth = height * sourceAspect
    offsetX = (width - renderedWidth) / 2
  } else {
    renderedWidth = width
    renderedHeight = width / sourceAspect
    offsetY = (height - renderedHeight) / 2
  }
  return {
    x: offsetX + (1 - point.x) * renderedWidth,
    y: offsetY + point.y * renderedHeight,
  }
}

function clearResultTimers() {
  clearTimeout(state.resultRevealTimer)
  clearTimeout(state.resultDismissTimer)
  state.resultRevealTimer = null
  state.resultDismissTimer = null
}

function dismissResultCard() {
  clearResultTimers()
  resultCard.classList.remove('visible')
  state.spell = null
  state.particles = []
  app.classList.remove('casting')
  setPhase('playing')
  if (state.mode === 'camera') requireFive('準備下一次施法')
  else beginTouchFlow()
}

function resetStroke(silent = false) {
  clearResultTimers()
  clearTimeout(state.messageTimer)
  clearTimeout(state.beautifyTimer)
  clearTimeout(state.strokePauseTimer)
  state.messageTimer = null
  state.beautifyTimer = null
  state.strokePauseTimer = null
  state.strokePausedAt = null
  state.completionFistFrames = 0
  state.completionOpenFrames = 0
  state.castGestureStartedAt = null
  state.stroke = []
  state.beautify = null
  state.wholeAnalysis = null
  state.drawing = false
  state.activeFrames = 0
  state.inactiveFrames = 0
  state.smoothedTip = null
  state.lastEvaluation = null
  spellProgress.style.width = '0%'
  setStrokeFeedback('尚未開始', 'idle')
  if (!silent && state.phase === 'playing') {
    const cameraMain = state.gestureStage === 'awaitingFive' ? '先比出 Five' : '伸出一根食指'
    const cameraSub = state.gestureStage === 'awaitingFive' ? '張開手掌並保持一下' : '讓手掌保持在鏡頭中央'
    setInstruction('準備', state.mode === 'camera' ? cameraMain : '在畫面上畫一個大圈', state.mode === 'camera' ? cameraSub : '回到起點就能完成施法')
  }
}

function requireFive(sub = '張開手掌並保持一下') {
  state.outerStroke = []
  state.bridgeStroke = []
  state.centralStroke = []
  state.outerEvaluation = null
  state.strokeKind = 'whole'
  state.sigilResult = null
  state.stableShape = null
  state.stableFrames = 0
  state.bridgeEntered = false
  state.heartCandidateStroke = []
  state.landmarkLostAt = null
  state.totalStrokePoints = 0
  state.attemptRecorded = false
  state.selectedElement = null
  app.removeAttribute('data-element')
  resetStroke(true)
  state.gestureStage = 'awaitingFive'
  state.fiveFrames = 0
  state.armedAt = 0
  setInstruction('啟動手勢', '先比出 Five', sub)
  setStrokeFeedback('等待 Five 啟動', 'idle')
  setStatus('等待 Five', 'idle')
  setCastStep('five')
  setGestureCue('five', '張開五指 · 保持一下')
}

function beginTouchFlow() {
  resetStroke(true)
  state.outerStroke = []
  state.bridgeStroke = []
  state.centralStroke = []
  state.outerEvaluation = null
  state.strokeKind = 'whole'
  state.sigilResult = null
  state.stableShape = null
  state.stableFrames = 0
  state.bridgeEntered = false
  state.heartCandidateStroke = []
  state.totalStrokePoints = 0
  state.attemptRecorded = false
  state.selectedElement = null
  state.gestureStage = 'ready'
  app.removeAttribute('data-element')
  setCastStep('outer')
  setGestureCue('hidden')
  setInstruction('連續成形', '從外圈畫到中央符號', '短暫放開可續畫；停下 1.2 秒後辨識')
  setStrokeFeedback('等待開始描繪', 'idle')
  setStatus('觸控 · 等待描繪', 'practice')
}

function addStrokePoint(point) {
  const previous = state.stroke.at(-1)
  if (previous && distance(previous, point) < 3.2) return
  state.stroke.push(point)
  state.totalStrokePoints += 1
  if (state.totalStrokePoints > 700) {
    state.drawing = false
    finishStroke('筆跡太長，請用較簡潔的路徑完成')
    return
  }
  spellProgress.style.width = `${Math.min(96, Math.round(state.totalStrokePoints / 1.8))}%`
  setStrokeFeedback('正在記錄連續軌跡', 'drawing')
}

function addStrokeSegment(point) {
  const previous = state.stroke.at(-1)
  if (!previous) {
    addStrokePoint(point)
    return
  }
  const steps = Math.max(1, Math.ceil(distance(previous, point) / 6))
  for (let index = 1; index <= steps; index += 1) {
    if (!state.drawing || state.phase !== 'playing') break
    addStrokePoint({
      x: previous.x + (point.x - previous.x) * (index / steps),
      y: previous.y + (point.y - previous.y) * (index / steps),
    })
  }
}

function clearStrokePause() {
  clearTimeout(state.strokePauseTimer)
  state.strokePauseTimer = null
  state.strokePausedAt = null
}

function beginAwaitingCastOpen() {
  clearStrokePause()
  state.drawing = false
  state.gestureStage = 'awaitingCastOpen'
  state.completionFistFrames = 2
  state.completionOpenFrames = 0
  state.castGestureStartedAt = performance.now()
  setGestureCue('open', '現在張開手掌 · 施放魔法')
  setStrokeFeedback('握拳確認完成 · 等待張掌', 'success')
  setInstruction('收筆確認', '現在張開手掌', '手掌穩定張開後才會辨識並施法')
  setStatus('等待施法動作', 'loading')
}

function completeCameraCast(reason = '完成後仍無法辨識完整咒式') {
  state.completionOpenFrames = 0
  state.castGestureStartedAt = null
  setGestureCue('hidden')
  if (!finalizeStroke()) finishStroke(state.wholeAnalysis?.reason || reason)
}

function scheduleStrokeFinalize(reason = '完成後仍無法辨識完整咒式') {
  if (state.phase !== 'playing' || state.stroke.length < 2) return
  clearStrokePause()
  state.drawing = false
  state.gestureStage = 'paused'
  state.strokePausedAt = performance.now()
  setStrokeFeedback('短暫停筆 · 軌跡保留中', 'near')
  setInstruction('可以繼續補畫', '短暫放開沒關係', '1.2 秒內接著畫，仍算同一次施法')
  setStatus('等待續畫', 'loading')
  state.strokePauseTimer = setTimeout(() => {
    state.strokePauseTimer = null
    state.strokePausedAt = null
    if (state.phase !== 'playing' || state.gestureStage !== 'paused') return
    if (state.mode === 'camera') {
      state.gestureStage = 'awaitingCastFist'
      state.completionFistFrames = 0
      state.castGestureStartedAt = performance.now()
      setGestureCue('fist', '畫完後握拳 · 確認收筆')
      setStrokeFeedback('軌跡已保留 · 等待握拳', 'near')
      setInstruction('完成圖形了嗎？', '握拳確認收筆', '如果還要補畫，重新伸出食指即可')
      setStatus('等待握拳', 'loading')
      return
    }
    if (!finalizeStroke()) finishStroke(state.wholeAnalysis?.reason || reason)
  }, STROKE_CONTINUE_GRACE_MS)
}

function resumeStroke(point) {
  if (state.phase !== 'playing' || !['paused', 'awaitingCastFist', 'awaitingCastOpen'].includes(state.gestureStage)) return false
  clearStrokePause()
  state.gestureStage = 'drawing'
  state.drawing = true
  state.completionFistFrames = 0
  state.completionOpenFrames = 0
  state.castGestureStartedAt = null
  state.landmarkLostAt = null
  addStrokeSegment(point)
  setStrokeFeedback('已接續同一次施法', 'drawing')
  setInstruction('繼續補畫', '完成外圈與中央符號', '短暫斷筆已自動接回')
  setStatus('正在續畫', 'drawing')
  return true
}

function finalizeStroke() {
  clearStrokePause()
  if (state.phase !== 'playing' || state.stroke.length < 2) return false
  const analysis = analyzeWholeStroke(state.stroke, {
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  })
  state.wholeAnalysis = analysis
  state.sigilResult = analysis.classification || {
    ok: false, shape: 'unknown', element: null, confidence: 0, reason: analysis.reason,
  }
  if (!analysis.ok) return false
  state.outerStroke = [...analysis.outerPoints]
  state.bridgeStroke = [...analysis.bridgePoints]
  state.centralStroke = [...analysis.centralPoints]
  state.outerEvaluation = analysis.outer
  state.lastEvaluation = analysis.outer
  applyElement(analysis.classification.element)
  beginBeautify(analysis)
  return true
}

function makeCleanSigil(shape, center, radius) {
  const scale = radius / 160
  return makeSigilFixture(shape).map((point) => ({
    x: center.x + (point.x - 100) * scale,
    y: center.y + (point.y - 100) * scale,
  }))
}

function beginBeautify(analysis) {
  clearTimeout(state.beautifyTimer)
  state.drawing = false
  state.gestureStage = 'beautifying'
  state.castGestureStartedAt = null
  setGestureCue('hidden')
  state.beautify = {
    start: performance.now(),
    duration: 760,
    rawPoints: [...state.stroke],
    cleanOuter: makeCirclePoints(analysis.outer.center, analysis.outer.radius, 96),
    cleanCentral: makeCleanSigil(analysis.classification.shape, analysis.outer.center, analysis.outer.radius),
  }
  setPhase('beautifying')
  spellProgress.style.width = '100%'
  setCastStep('sigil')
  setInstruction('整筆完成', '正在收束你的魔法陣', '原始筆跡會化成完整外圈與中央符號')
  setStrokeFeedback(`${analysis.classification.shape} · 正在成形`, 'success')
  setStatus('咒式成形中', 'loading')
  state.beautifyTimer = setTimeout(() => {
    if (state.phase !== 'beautifying') return
    castSpell(analysis.outer)
  }, state.beautify.duration)
}

function finishStroke(reason = '連續施法軌跡中斷') {
  if (state.phase !== 'playing') return
  state.drawing = false
  state.sigilResult ||= { ok: false, shape: 'unknown', element: null, confidence: 0, reason }
  recordTraceAttempt('failed', reason)
  setInstruction('施法未完成', reason, state.mode === 'camera' ? '請再比一次 Five 後重新描繪' : '請重新描繪後再放開')
  setStrokeFeedback(reason, 'fail')
  setStatus('施法未完成', 'idle')
  clearTimeout(state.messageTimer)
  state.messageTimer = setTimeout(() => {
    if (state.mode === 'camera') requireFive('請重新以 Five 啟動')
    else beginTouchFlow()
  }, 650)
}

function createParticles(center, radius, elementKey) {
  const element = ELEMENTS[elementKey]
  const count = elementKey === 'earth' ? 86 : elementKey === 'wind' ? 108 : 132
  return Array.from({ length: count }, (_, index) => {
    const angle = (index / count) * Math.PI * 2 + Math.random() * 0.28
    const speed = elementKey === 'earth' ? .7 + Math.random() * 2.1 : 1.2 + Math.random() * 3.8
    const tangent = elementKey === 'wind' ? (Math.random() > .5 ? 1 : -1) * (1.2 + Math.random() * 2.4) : 0
    return {
      x: center.x + Math.cos(angle) * radius * (0.15 + Math.random() * 0.8),
      y: center.y + Math.sin(angle) * radius * (0.15 + Math.random() * 0.8),
      vx: Math.cos(angle) * speed - Math.sin(angle) * tangent,
      vy: Math.sin(angle) * speed + Math.cos(angle) * tangent,
      size: (elementKey === 'earth' ? 3 : 1.6) + Math.random() * (elementKey === 'earth' ? 5.8 : 4.2),
      life: 0.7 + Math.random() * 0.3,
      color: element.colors[index % element.colors.length],
      rotation: Math.random() * Math.PI,
      spin: (Math.random() - .5) * .16,
      kind: elementKey,
    }
  })
}

function unlockAudio() {
  try {
    state.audio ||= new (window.AudioContext || window.webkitAudioContext)()
    if (state.audio.state === 'suspended') state.audio.resume().catch(() => {})
  } catch { state.audio = null }
}

function noiseBurst(context, now, { duration, gainValue, filterType, frequency }) {
  const buffer = context.createBuffer(1, Math.max(1, Math.floor(context.sampleRate * duration)), context.sampleRate)
  const data = buffer.getChannelData(0)
  for (let index = 0; index < data.length; index += 1) data[index] = Math.random() * 2 - 1
  const source = context.createBufferSource(); source.buffer = buffer
  const filter = context.createBiquadFilter(); filter.type = filterType; filter.frequency.value = frequency
  const gain = context.createGain(); gain.gain.setValueAtTime(gainValue, now); gain.gain.exponentialRampToValueAtTime(0.0001, now + duration)
  source.connect(filter).connect(gain).connect(context.destination)
  source.start(now); source.stop(now + duration)
}

function castTone(elementKey) {
  try {
    if (state.muted) return
    unlockAudio()
    if (!state.audio) return
    const audio = state.audio
    const now = audio.currentTime + 0.01
    const spec = getSpellAudioSpec(elementKey)
    if (!spec) return
    spec.voices.forEach((voice) => {
      const oscillator = audio.createOscillator(); const gain = audio.createGain()
      oscillator.type = voice.type
      const start = now + voice.offset
      oscillator.frequency.setValueAtTime(voice.frequency, start)
      oscillator.frequency.exponentialRampToValueAtTime(voice.endFrequency, start + voice.release)
      gain.gain.setValueAtTime(0.0001, start)
      gain.gain.exponentialRampToValueAtTime(voice.gain, start + voice.attack)
      gain.gain.exponentialRampToValueAtTime(0.0001, start + voice.release)
      oscillator.connect(gain).connect(audio.destination)
      oscillator.start(start); oscillator.stop(start + voice.release + 0.04)
    })
    if (spec.noise) noiseBurst(audio, now + spec.noise.offset, {
      duration: spec.noise.duration,
      gainValue: spec.noise.gain,
      filterType: spec.noise.filter,
      frequency: spec.noise.frequency,
    })
    spec.pulses.forEach((pulse) => {
      const oscillator = audio.createOscillator(); const gain = audio.createGain()
      const start = now + pulse.offset
      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(pulse.frequency, start)
      oscillator.frequency.exponentialRampToValueAtTime(pulse.endFrequency, start + pulse.duration)
      gain.gain.setValueAtTime(.0001, start)
      gain.gain.exponentialRampToValueAtTime(pulse.gain, start + .012)
      gain.gain.exponentialRampToValueAtTime(.0001, start + pulse.duration)
      oscillator.connect(gain).connect(audio.destination)
      oscillator.start(start); oscillator.stop(start + pulse.duration + .02)
    })
    state.lastAudioSchedule = {
      element: elementKey,
      profile: spec.profile,
      voiceCount: spec.voices.length,
      noiseFilter: spec.noise?.filter ?? null,
      pulseCount: spec.pulses.length,
      duration: spec.duration,
    }
  } catch (error) {
    state.lastAudioError = error instanceof Error ? error.message : String(error)
    console.warn('Spell audio unavailable', error)
    /* Audio is optional and must never block casting. */
  }
}

function castSpell(evaluation) {
  if (!['playing', 'beautifying'].includes(state.phase)) return
  clearResultTimers()
  resultCard.classList.remove('visible')
  recordTraceAttempt('cast')
  setPhase('casting')
  setGestureCue('hidden')
  state.gestureStage = 'cooldown'
  state.castCount += 1
  state.cooldownUntil = performance.now() + 2200
  const element = ELEMENTS[state.selectedElement]
  state.spell = {
    center: evaluation.center,
    radius: Math.max(72, evaluation.radius),
    start: performance.now(),
    clockwise: evaluation.clockwise,
    element: state.selectedElement,
  }
  state.particles = createParticles(state.spell.center, state.spell.radius, state.selectedElement)
  state.stroke = []
  state.drawing = false
  spellProgress.style.width = '100%'
  setStrokeFeedback(`完成！${element.rune}元素成形`, 'success')
  setInstruction(`${element.rune}元素成形`, element.name, '你親手完成了這道咒式')
  setStatus(`${element.rune} · ${element.name}`, 'success')
  resultRune.textContent = element.rune
  resultKicker.textContent = element.kicker
  resultName.textContent = element.name
  resultCopy.innerHTML = element.copy
  app.classList.add('casting')
  state.lastAudioSchedule = null
  state.lastAudioError = null
  castTone(state.selectedElement)
  state.resultRevealTimer = setTimeout(() => {
    state.resultRevealTimer = null
    resultCard.classList.add('visible')
    setPhase('result')
    state.resultDismissTimer = setTimeout(dismissResultCard, RESULT_CARD_VISIBLE_MS)
  }, 1550)
}

function installWasmMimeFetchRepair() {
  if (state.wasmMimeFetchInstalled) return
  const nativeFetch = window.fetch.bind(window)
  window.fetch = async (...args) => {
    const response = await nativeFetch(...args)
    const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url
    if (!requestUrl || !new URL(requestUrl, location.href).pathname.endsWith('.wasm') || response.headers.get('content-type')?.startsWith('application/wasm')) return response
    const headers = new Headers(response.headers)
    headers.set('content-type', 'application/wasm')
    return new Response(response.body, { status: response.status, statusText: response.statusText, headers })
  }
  state.wasmMimeFetchInstalled = true
}

async function loadHandModel() {
  if (state.handLandmarker) return
  installWasmMimeFetchRepair()
  setStatus('載入手勢模型', 'loading')
  setInstruction('正在準備', '喚醒工作室…', '第一次載入約 8 MB')
  const base = new URL(import.meta.env.BASE_URL, location.href)
  const vision = await FilesetResolver.forVisionTasks(new URL('wasm', base).href)
  const options = {
    baseOptions: { modelAssetPath: new URL('models/hand_landmarker.task', base).href, delegate: 'GPU' },
    runningMode: 'VIDEO',
    numHands: 1,
    minHandDetectionConfidence: 0.55,
    minHandPresenceConfidence: 0.5,
    minTrackingConfidence: 0.5,
  }
  try {
    state.handLandmarker = await HandLandmarker.createFromOptions(vision, options)
  } catch (gpuError) {
    console.warn('GPU delegate unavailable; falling back to CPU', gpuError)
    options.baseOptions.delegate = 'CPU'
    state.handLandmarker = await HandLandmarker.createFromOptions(vision, options)
  }
  state.modelReady = true
}

async function startCamera() {
  errorCard.classList.remove('visible')
  intro.classList.add('hidden')
  playUi.classList.add('visible')
  setPhase('loading')
  state.mode = 'camera'
  app.classList.remove('practice')
  if (!window.isSecureContext && !['localhost', '127.0.0.1'].includes(location.hostname)) {
    throw new Error('INSECURE_CONTEXT')
  }
  if (!navigator.mediaDevices?.getUserMedia) throw new Error('CAMERA_UNAVAILABLE')
  state.stream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: 'user',
      width: { ideal: 640, max: 960 },
      height: { ideal: 480, max: 720 },
      frameRate: { ideal: 30, max: 30 },
    },
  })
  video.srcObject = state.stream
  await video.play()
  await loadHandModel()
  setPhase('playing')
  requireFive()
  startAnimation()
}

function startPractice() {
  intro.classList.add('hidden')
  errorCard.classList.remove('visible')
  resultCard.classList.remove('visible')
  playUi.classList.add('visible')
  state.mode = 'practice'
  app.classList.add('practice')
  setPhase('playing')
  beginTouchFlow()
  startAnimation()
}

function cameraFailure(error) {
  console.error(error)
  setPhase('error')
  playUi.classList.remove('visible')
  errorCard.classList.add('visible')
  if (error?.message === 'INSECURE_CONTEXT') {
    errorCopy.textContent = '手機鏡頭需要 HTTPS。請使用 HTTPS 網址後再試一次。'
  } else if (error?.name === 'NotAllowedError') {
    errorCopy.textContent = '相機權限被拒絕。請在瀏覽器網站設定中允許相機後重試。'
  } else {
    errorCopy.textContent = '請確認相機沒有被其他 App 使用，並允許瀏覽器存取。'
  }
  setStatus('鏡頭未連線', 'error')
}

async function detectHand(now) {
  if (state.mode !== 'camera' || state.phase !== 'playing' || !state.handLandmarker || video.readyState < 2) return
  if (now - state.lastInferenceAt < 48 || video.currentTime === state.lastVideoTime) return
  state.lastInferenceAt = now
  state.lastVideoTime = video.currentTime
  const detection = state.handLandmarker.detectForVideo(video, now)
  const landmarks = detection.landmarks?.[0]
  state.landmarks = landmarks || null

  if (!landmarks) {
    state.fiveFrames = 0
    state.activeFrames = 0
    state.inactiveFrames += 1
    if (state.drawing) scheduleStrokeFinalize('手勢離開鏡頭太久')
    else if (state.gestureStage === 'paused') setStrokeFeedback('短暫失去指尖 · 1.2 秒內可繼續', 'near')
    else if (['awaitingCastFist', 'awaitingCastOpen'].includes(state.gestureStage)) {
      setStrokeFeedback(state.gestureStage === 'awaitingCastFist' ? '等待握拳確認' : '等待張開手掌施法', 'near')
    }
    if (state.gestureStage === 'awaitingFive') {
      setInstruction('啟動手勢', '先比出 Five', '把張開的手掌移到鏡頭中央')
    } else if (state.gestureStage === 'armed') {
      setInstruction('魔杖已準備', '伸出一根食指', '五秒內開始描繪')
    }
    return
  }

  const openPalm = isOpenPalm(landmarks)
  const pointing = isPointingHand(landmarks)
  const fist = isFist(landmarks)
  state.landmarkLostAt = null

  if (state.gestureStage === 'paused') {
    if (fist) {
      state.completionFistFrames += 1
      if (state.completionFistFrames >= 2) beginAwaitingCastOpen()
    } else if (pointing) {
      const tip = mapLandmark(landmarks[8])
      state.smoothedTip = smoothPoint(state.smoothedTip, tip)
      resumeStroke(state.smoothedTip)
    } else {
      state.completionFistFrames = 0
    }
    return
  }

  if (state.gestureStage === 'awaitingCastFist') {
    if (fist) {
      state.completionFistFrames += 1
      if (state.completionFistFrames >= 2) beginAwaitingCastOpen()
    } else if (pointing) {
      const tip = mapLandmark(landmarks[8])
      state.smoothedTip = smoothPoint(state.smoothedTip, tip)
      resumeStroke(state.smoothedTip)
    } else {
      state.completionFistFrames = 0
    }
    return
  }

  if (state.gestureStage === 'awaitingCastOpen') {
    if (openPalm) {
      state.completionOpenFrames += 1
      if (state.completionOpenFrames >= 2) completeCameraCast()
    } else if (pointing) {
      state.activeFrames += 1
      if (state.activeFrames >= 2) {
        const tip = mapLandmark(landmarks[8])
        state.smoothedTip = smoothPoint(state.smoothedTip, tip)
        resumeStroke(state.smoothedTip)
      }
    } else {
      state.activeFrames = 0
      state.completionOpenFrames = 0
    }
    return
  }

  if (state.gestureStage === 'awaitingFive') {
    state.drawing = false
    state.activeFrames = 0
    state.inactiveFrames = 0
    if (openPalm) {
      state.fiveFrames += 1
      const progress = Math.min(100, state.fiveFrames * 25)
      spellProgress.style.width = `${progress}%`
      setInstruction('啟動訊號', '保持 Five', state.fiveFrames >= 3 ? '收到，魔杖正在甦醒' : '張開五指，保持一下')
      setStrokeFeedback('正在確認 Five', 'near')
      setStatus('偵測 Five', 'loading')
      if (state.fiveFrames >= 4) {
        resetStroke(true)
        state.gestureStage = 'armed'
        state.armedAt = performance.now()
        setInstruction('魔杖已準備', '現在伸出一根食指', '開始畫時才會留下軌跡')
        setStrokeFeedback('Five 已確認 · 等待食指', 'success')
        setStatus('魔杖已舉起', 'success')
        setCastStep('outer')
        setGestureCue('draw', '魔杖已準備 · 用食指畫外圈')
      }
    } else {
      state.fiveFrames = 0
      spellProgress.style.width = '0%'
      setInstruction('啟動手勢', '先比出 Five', '未啟動前不會記錄食指軌跡')
      setStrokeFeedback('等待 Five 啟動', 'idle')
      setStatus('等待 Five', 'idle')
    }
    return
  }


  if (state.gestureStage === 'armed') {
    if (performance.now() - state.armedAt > 6000) {
      requireFive('準備逾時，請再比一次 Five')
      return
    }
    state.inactiveFrames = 0
    if (pointing) {
      state.activeFrames += 1
      const tip = mapLandmark(landmarks[8])
      state.smoothedTip = smoothPoint(state.smoothedTip, tip)
      if (state.activeFrames >= 2 && performance.now() > state.cooldownUntil) {
        state.gestureStage = 'drawing'
        state.strokeKind = 'whole'
        setGestureCue('hidden')
        state.drawing = true
        addStrokePoint(state.smoothedTip)
        setInstruction('連續成形', '從外圈畫到中央符號', '完成後握拳，再張開手掌施法')
        setStatus('正在描繪', 'drawing')
      }
    } else {
      state.activeFrames = 0
      state.smoothedTip = null
      setInstruction('魔杖已準備', '現在伸出一根食指', '開始畫時才會留下軌跡')
      setStrokeFeedback('Five 已確認 · 等待食指', 'success')
    }
    return
  }

  if (state.gestureStage === 'drawing') {
    if (fist) {
      state.completionFistFrames += 1
      state.activeFrames = 0
      if (state.completionFistFrames >= 2) beginAwaitingCastOpen()
    } else if (pointing) {
      state.completionFistFrames = 0
      state.activeFrames += 1
      state.inactiveFrames = 0
      const tip = mapLandmark(landmarks[8])
      state.smoothedTip = smoothPoint(state.smoothedTip, tip)
      state.drawing = true
      addStrokePoint(state.smoothedTip)
      setInstruction('連續成形', '畫完外圈與中央符號', '完成後握拳，再張開手掌施法')
      setStatus('正在記錄連續軌跡', 'drawing')
    } else {
      state.completionFistFrames = 0
      state.activeFrames = 0
      state.inactiveFrames += 1
      if (state.drawing) scheduleStrokeFinalize('食指手勢中斷太久')
    }
  }
}

const connections = [[0,1],[1,2],[2,3],[3,4],[0,5],[5,6],[6,7],[7,8],[5,9],[9,10],[10,11],[11,12],[9,13],[13,14],[14,15],[15,16],[13,17],[17,18],[18,19],[19,20],[0,17]]

function drawHand() {
  if (!state.landmarks || state.mode !== 'camera') return
  const points = state.landmarks.map(mapLandmark)
  ctx.save()
  ctx.lineCap = 'round'
  ctx.lineWidth = 1.2
  ctx.strokeStyle = 'rgba(140, 226, 255, .35)'
  connections.forEach(([a, b]) => {
    ctx.beginPath(); ctx.moveTo(points[a].x, points[a].y); ctx.lineTo(points[b].x, points[b].y); ctx.stroke()
  })
  points.forEach((point, index) => {
    ctx.beginPath(); ctx.arc(point.x, point.y, index === 8 ? 5 : 2.2, 0, Math.PI * 2)
    ctx.fillStyle = index === 8 ? '#fff3b8' : 'rgba(160,235,255,.72)'; ctx.fill()
  })
  if (state.smoothedTip) {
    ctx.shadowColor = '#baf5ff'; ctx.shadowBlur = 18
    ctx.beginPath(); ctx.arc(state.smoothedTip.x, state.smoothedTip.y, 10 + Math.sin(performance.now() / 90) * 2, 0, Math.PI * 2)
    ctx.strokeStyle = '#efffff'; ctx.lineWidth = 2; ctx.stroke()
  }
  ctx.restore()
}

function drawStroke() {
  const paths = []
  if (state.stroke.length >= 2) paths.push({ points: state.stroke, locked: false })
  if (!paths.length && !state.outerEvaluation) return
  ctx.save()
  ctx.lineCap = 'round'; ctx.lineJoin = 'round'
  const gradient = ctx.createLinearGradient(0, 0, window.innerWidth, window.innerHeight)
  gradient.addColorStop(0, '#f6d184'); gradient.addColorStop(0.55, '#c9fbff'); gradient.addColorStop(1, '#74cfff')
  const morphProgress = state.beautify
    ? Math.min(1, Math.max(0, (performance.now() - state.beautify.start) / state.beautify.duration))
    : 0
  if (state.outerEvaluation) {
    const { center, radius } = state.outerEvaluation
    const lockedColor = state.selectedElement ? ELEMENTS[state.selectedElement].color : '#c9fbff'
    ctx.globalAlpha = state.beautify ? Math.max(.08, morphProgress) : 1
    ctx.shadowColor = lockedColor; ctx.shadowBlur = state.phase === 'casting' ? 18 : 25
    ctx.strokeStyle = lockedColor; ctx.lineWidth = state.phase === 'casting' ? 3.5 : 5
    ctx.beginPath(); ctx.arc(center.x, center.y, radius, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * (state.beautify ? morphProgress : 1)); ctx.stroke()
    ctx.shadowBlur = 6; ctx.strokeStyle = 'rgba(255,255,255,.86)'; ctx.lineWidth = 1.2; ctx.stroke()
    const clean = state.beautify?.cleanCentral || []
    const visiblePoints = clean.slice(0, Math.max(2, Math.ceil(clean.length * morphProgress)))
    if (visiblePoints.length >= 2) {
      ctx.shadowColor = lockedColor; ctx.shadowBlur = 22
      ctx.strokeStyle = lockedColor; ctx.lineWidth = 5
      ctx.beginPath(); ctx.moveTo(visiblePoints[0].x, visiblePoints[0].y)
      visiblePoints.slice(1).forEach((point) => ctx.lineTo(point.x, point.y))
      ctx.stroke()
      ctx.shadowBlur = 5; ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.lineWidth = 1.15; ctx.stroke()
    }
  }
  for (const path of paths) {
    ctx.globalAlpha = state.beautify ? Math.max(0, 1 - morphProgress) : 1
    ctx.shadowColor = '#84e8ff'; ctx.shadowBlur = 20
    ctx.strokeStyle = gradient; ctx.lineWidth = 4
    ctx.beginPath(); ctx.moveTo(path.points[0].x, path.points[0].y)
    path.points.slice(1).forEach((point) => ctx.lineTo(point.x, point.y))
    ctx.stroke()
    ctx.shadowBlur = 7; ctx.lineWidth = 1.2; ctx.strokeStyle = '#ffffff'; ctx.stroke()
  }
  ctx.globalAlpha = 1
  if (state.phase === 'playing' && state.drawing && state.stroke.length) {
    const start = state.stroke[0]
    const targetRadius = 18 + Math.sin(performance.now() / 110) * 2
    ctx.setLineDash([4, 7])
    ctx.shadowBlur = 0
    ctx.shadowColor = '#84e8ff'
    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(255,240,188,.65)'
    ctx.beginPath(); ctx.arc(start.x, start.y, targetRadius, 0, Math.PI * 2); ctx.stroke()
  }
  ctx.restore()
}

function polygonPath(sides, radius, rotation = 0) {
  ctx.beginPath()
  for (let index = 0; index < sides; index += 1) {
    const angle = rotation + index / sides * Math.PI * 2
    const x = Math.cos(angle) * radius; const y = Math.sin(angle) * radius
    if (index === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
  }
  ctx.closePath()
}

function drawElementSignature(elementKey, radius, elapsed, reveal, direction) {
  const element = ELEMENTS[elementKey]
  ctx.save()
  ctx.strokeStyle = element.bright
  ctx.fillStyle = element.color
  ctx.shadowColor = element.color
  ctx.shadowBlur = 24

  if (elementKey === 'love') {
    const pulse = 1 + Math.sin(elapsed * 5.4) * .055
    ctx.scale(pulse * reveal, pulse * reveal)
    for (let ring = 0; ring < 3; ring += 1) {
      const scale = radius * (.021 + ring * .0048)
      ctx.save(); ctx.scale(scale, -scale); ctx.translate(0, -2)
      ctx.globalAlpha = .9 - ring * .24; ctx.lineWidth = 3.4 / scale
      ctx.beginPath()
      for (let index = 0; index <= 80; index += 1) {
        const t = index / 80 * Math.PI * 2
        const x = 16 * Math.sin(t) ** 3
        const y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)
        if (index === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
      }
      ctx.closePath(); ring === 0 ? ctx.fill() : ctx.stroke(); ctx.restore()
    }
  } else if (elementKey === 'fire') {
    ctx.rotate(elapsed * .34 * direction)
    for (let index = 0; index < 9; index += 1) {
      const angle = index / 9 * Math.PI * 2
      ctx.save(); ctx.rotate(angle); ctx.translate(radius * .58 * reveal, 0)
      const flicker = 1 + Math.sin(elapsed * 12 + index) * .12
      ctx.globalAlpha = .42 + reveal * .45
      ctx.beginPath(); ctx.moveTo(-radius * .09, 0)
      ctx.quadraticCurveTo(0, -radius * .15 * flicker, radius * .2, 0)
      ctx.quadraticCurveTo(0, radius * .09, -radius * .09, 0); ctx.fill(); ctx.restore()
    }
    ctx.globalAlpha = .9
    ctx.beginPath(); ctx.arc(0, 0, radius * (.16 + Math.sin(elapsed * 10) * .015), 0, Math.PI * 2); ctx.fill()
  } else if (elementKey === 'water') {
    for (let ring = 0; ring < 3; ring += 1) {
      const waveRadius = radius * (.28 + ring * .2) * reveal + Math.sin(elapsed * 3.2 + ring) * 3
      ctx.globalAlpha = .82 - ring * .17
      ctx.lineWidth = 3.2 - ring * .6
      ctx.beginPath()
      for (let index = 0; index <= 72; index += 1) {
        const angle = index / 72 * Math.PI * 2
        const wobble = Math.sin(angle * 3 - elapsed * 4 + ring) * radius * .025
        const x = Math.cos(angle) * (waveRadius + wobble); const y = Math.sin(angle) * (waveRadius + wobble)
        if (index === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
      }
      ctx.stroke()
    }
    ctx.globalAlpha = .9; ctx.rotate(Math.sin(elapsed * 1.8) * .18)
    ctx.beginPath(); ctx.moveTo(0, -radius * .32); ctx.bezierCurveTo(radius * .23, -radius * .03, radius * .17, radius * .2, 0, radius * .26); ctx.bezierCurveTo(-radius * .17, radius * .2, -radius * .23, -radius * .03, 0, -radius * .32); ctx.fill()
  } else if (elementKey === 'wind') {
    ctx.lineCap = 'round'
    for (let index = 0; index < 7; index += 1) {
      const angle = index / 7 * Math.PI * 2 + elapsed * (.32 + index * .015) * direction
      ctx.globalAlpha = .9 - index * .07; ctx.lineWidth = 4 - index * .28
      ctx.beginPath(); ctx.arc(0, 0, radius * (.3 + index * .08) * reveal, angle, angle + Math.PI * (1.08 + index * .035)); ctx.stroke()
    }
    ctx.globalAlpha = .72; ctx.lineWidth = 2
    ctx.beginPath(); ctx.arc(0, 0, radius * .18, elapsed * direction, elapsed * direction + Math.PI * 1.55); ctx.stroke()
  } else {
    ctx.rotate(elapsed * .06 * direction)
    ctx.globalAlpha = .9; ctx.lineWidth = 4
    polygonPath(6, radius * .58 * reveal, Math.PI / 6); ctx.stroke()
    ctx.globalAlpha = .38; ctx.lineWidth = 9
    polygonPath(6, radius * .38 * reveal, 0); ctx.stroke()
    for (let index = 0; index < 6; index += 1) {
      const angle = index / 6 * Math.PI * 2
      ctx.save(); ctx.rotate(angle); ctx.translate(radius * .7 * reveal, 0); ctx.rotate(Math.PI / 4)
      ctx.globalAlpha = .72; ctx.fillRect(-radius * .06, -radius * .06, radius * .12, radius * .12); ctx.restore()
    }
  }
  ctx.restore()
}

function drawSpell(now) {
  if (!state.spell) return
  const elapsed = Math.max(0, (now - state.spell.start) / 1000)
  const { center, radius, element: elementKey } = state.spell
  const element = ELEMENTS[elementKey]
  const direction = state.spell.clockwise ? 1 : -1
  const reveal = Math.min(1, elapsed * 2.4)
  const fade = Math.max(0, Math.min(1, 3.8 - elapsed))
  ctx.save()
  ctx.translate(center.x, center.y)
  ctx.globalCompositeOperation = 'lighter'
  ctx.globalAlpha = fade
  const core = ctx.createRadialGradient(0, 0, 0, 0, 0, radius * 1.14)
  core.addColorStop(0, elementKey === 'earth' ? 'rgba(255,224,164,.5)' : element.soft)
  core.addColorStop(.35, element.soft)
  core.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = core
  ctx.beginPath(); ctx.arc(0, 0, radius * 1.14 * reveal, 0, Math.PI * 2); ctx.fill()
  ctx.shadowColor = element.color; ctx.shadowBlur = 38
  ;[1, .72, .45].forEach((scale, index) => {
    ctx.save(); ctx.rotate(direction * elapsed * (0.16 + index * 0.11))
    ctx.strokeStyle = index === 1 ? element.bright : element.color
    ctx.globalAlpha = fade * (.82 - index * .08)
    ctx.lineWidth = 2.8 + index * .7
    ctx.setLineDash(index === 0 ? [10, 8] : index === 1 ? [2, 9] : [])
    ctx.beginPath(); ctx.arc(0, 0, radius * scale * reveal, 0, Math.PI * 2); ctx.stroke(); ctx.restore()
  })
  ctx.setLineDash([])
  drawElementSignature(elementKey, radius, elapsed, reveal, direction)
  ctx.restore()

  state.particles.forEach((particle) => {
    particle.x += particle.vx; particle.y += particle.vy; particle.rotation += particle.spin
    if (particle.kind === 'fire') { particle.vy -= .045; particle.vx *= .992 }
    else if (particle.kind === 'water') { particle.vy += .025; particle.vx *= .994 }
    else if (particle.kind === 'earth') { particle.vy += .042; particle.vx *= .982 }
    else { particle.vx *= 1.003; particle.vy *= 1.003 }
    particle.life -= particle.kind === 'earth' ? .008 : .006
    ctx.save(); ctx.translate(particle.x, particle.y); ctx.rotate(particle.rotation)
    ctx.globalAlpha = Math.max(0, particle.life) * fade
    ctx.fillStyle = particle.color; ctx.strokeStyle = particle.color; ctx.shadowColor = particle.color; ctx.shadowBlur = particle.kind === 'earth' ? 5 : 11
    if (particle.kind === 'wind') {
      ctx.lineWidth = Math.max(1, particle.size * .42); ctx.beginPath(); ctx.moveTo(-particle.size * 2.8, 0); ctx.quadraticCurveTo(0, -particle.size, particle.size * 2, 0); ctx.stroke()
    } else if (particle.kind === 'water') {
      ctx.beginPath(); ctx.ellipse(0, 0, particle.size * .62, particle.size * 1.45, 0, 0, Math.PI * 2); ctx.fill()
    } else if (particle.kind === 'earth') {
      ctx.beginPath(); ctx.moveTo(-particle.size, particle.size * .7); ctx.lineTo(-particle.size * .55, -particle.size); ctx.lineTo(particle.size * .75, -particle.size * .55); ctx.lineTo(particle.size, particle.size * .8); ctx.closePath(); ctx.fill()
    } else {
      ctx.beginPath(); ctx.moveTo(0, -particle.size * 1.8); ctx.quadraticCurveTo(particle.size * 1.2, 0, 0, particle.size); ctx.quadraticCurveTo(-particle.size * 1.2, 0, 0, -particle.size * 1.8); ctx.fill()
    }
    ctx.restore()
  })
  ctx.globalAlpha = 1; ctx.shadowBlur = 0
  if (elapsed > 4.2) { state.spell = null; state.particles = []; app.classList.remove('casting') }
}

function render(now) {
  resizeCanvas()
  ctx.clearRect(0, 0, window.innerWidth, window.innerHeight)
  detectHand(now)
  drawStroke()
  drawHand()
  drawSpell(now)
  if (debug) {
    debugPanel.hidden = false
    debugPanel.textContent = JSON.stringify({ phase: state.phase, mode: state.mode, points: state.stroke.length, castCount: state.castCount, modelReady: state.modelReady, score: state.lastEvaluation?.score ?? null }, null, 2)
  }
  requestAnimationFrame(render)
}

function startAnimation() {
  if (state.animationStarted) return
  state.animationStarted = true
  requestAnimationFrame(render)
}

let practicePointer = null
canvas.addEventListener('pointerdown', (event) => {
  if (state.mode !== 'practice' || state.phase !== 'playing' || !['ready', 'paused'].includes(state.gestureStage)) return
  const resuming = state.gestureStage === 'paused'
  practicePointer = event.pointerId
  canvas.setPointerCapture(event.pointerId)
  clearTimeout(state.messageTimer)
  state.messageTimer = null
  if (resuming) {
    resumeStroke({ x: event.clientX, y: event.clientY })
    return
  }
  state.stroke = []
  state.attemptRecorded = false
  state.drawing = true
  state.strokeKind = 'whole'
  state.gestureStage = 'drawing'
  addStrokePoint({ x: event.clientX, y: event.clientY })
  setInstruction('連續成形', '畫完外圈與中央符號', '短暫放開可續畫；停下 1.2 秒後辨識')
})
canvas.addEventListener('pointermove', (event) => {
  if (state.mode !== 'practice' || practicePointer !== event.pointerId || !state.drawing) return
  addStrokeSegment({ x: event.clientX, y: event.clientY })
})
canvas.addEventListener('pointerup', (event) => {
  if (practicePointer !== event.pointerId) return
  practicePointer = null
  if (state.drawing) scheduleStrokeFinalize('完成後仍無法辨識完整咒式')
})
canvas.addEventListener('pointercancel', () => {
  practicePointer = null
  if (state.drawing) scheduleStrokeFinalize('觸控軌跡中斷')
})

$('#start-camera').addEventListener('click', () => { unlockAudio(); startCamera().catch(cameraFailure) })
$('#start-practice').addEventListener('click', () => { unlockAudio(); startPractice() })
$('#fallback-practice').addEventListener('click', () => { unlockAudio(); startPractice() })
$('#retry-camera').addEventListener('click', () => { unlockAudio(); startCamera().catch(cameraFailure) })
exportTracesButton.addEventListener('click', exportLocalTraces)
clearTracesButton.addEventListener('click', () => {
  if (!readLocalTraces().length || !window.confirm('清除這台裝置上的所有軌跡資料？')) return
  try { clearTraceRecords(window.localStorage) } catch {}
  updateTraceTools()
})
$('#toggle-sigil-guide').addEventListener('click', (event) => {
  const expanded = event.currentTarget.getAttribute('aria-expanded') !== 'true'
  event.currentTarget.setAttribute('aria-expanded', String(expanded))
  sigilGuide.hidden = !expanded
})
$('#mute-audio').addEventListener('click', (event) => {
  state.muted = !state.muted
  event.currentTarget.setAttribute('aria-pressed', String(state.muted))
  event.currentTarget.textContent = state.muted ? '音效 OFF' : '音效 ON'
  if (!state.muted) unlockAudio()
})
$('#reset-stroke').addEventListener('click', () => {
  recordTraceAttempt('cancelled', 'player-cleared')
  if (state.mode === 'camera') requireFive('已清除，請再比一次 Five')
  else beginTouchFlow()
})
$('#cast-again').addEventListener('click', () => {
  dismissResultCard()
})
window.addEventListener('resize', resizeCanvas)
window.addEventListener('pagehide', () => state.stream?.getTracks().forEach((track) => track.stop()))

window.__MAGIC__ = {
  getState: () => ({
    build: BUILD_ID, phase: state.phase, mode: state.mode, gestureStage: state.gestureStage,
    castCount: state.castCount, modelReady: state.modelReady, selectedElement: state.selectedElement,
    activeElement: state.spell?.element ?? null, spellActive: Boolean(state.spell), points: state.stroke.length,
    outerPoints: state.outerStroke.length, outerLocked: Boolean(state.outerEvaluation), outerClosure: state.outerEvaluation?.closure ?? null, strokeKind: state.strokeKind,
    bridgePoints: state.bridgeStroke.length, centralPoints: state.centralStroke.length,
    pointerActive: practicePointer !== null, stableFrames: state.stableFrames,
    landmarkGraceMs: STROKE_CONTINUE_GRACE_MS, continuationGraceMs: STROKE_CONTINUE_GRACE_MS,
    cameraCompletionGesture: 'fist-open', completionWaitHasTimeout: false,
    resultCardVisibleMs: RESULT_CARD_VISIBLE_MS, resultDismissPending: Boolean(state.resultDismissTimer),
    completionFistFrames: state.completionFistFrames, completionOpenFrames: state.completionOpenFrames,
    strokePaused: state.gestureStage === 'paused',
    pauseRemainingMs: state.strokePausedAt ? Math.max(0, STROKE_CONTINUE_GRACE_MS - (performance.now() - state.strokePausedAt)) : 0,
    midStrokeConfirmation: 'none', totalStrokePoints: state.totalStrokePoints,
    traceCount: readLocalTraces().length,
    beautifyActive: state.phase === 'beautifying' && Boolean(state.beautify),
    morphProgress: state.beautify ? Math.min(1, Math.max(0, (performance.now() - state.beautify.start) / state.beautify.duration)) : 0,
    sigilResult: state.sigilResult, lastEvaluation: state.lastEvaluation,
    audioProfile: state.selectedElement ? SIGIL_SPELLS[Object.keys(SIGIL_SPELLS).find((shape) => SIGIL_SPELLS[shape].element === state.selectedElement)]?.audio.profile : null,
    audioSchedule: state.lastAudioSchedule,
    audioError: state.lastAudioError,
  }),
  simulateOuter: () => {
    if (state.phase === 'intro' || state.phase === 'error') startPractice()
    const points = makeCirclePoints(window.innerWidth / 2, window.innerHeight * .48, Math.min(window.innerWidth, window.innerHeight) * .26, 84, 5)
    return evaluateCircle(points, window.innerWidth, window.innerHeight)
  },
  simulateSigil: (shape) => window.__MAGIC__.simulateCast(shape),
  simulateCast: (shape = 'triangle') => {
    if (state.phase === 'result') {
      dismissResultCard()
    }
    if (state.phase === 'intro' || state.phase === 'error') startPractice()
    const fixture = makeOneStrokeFixture(shape, {
      center: { x: window.innerWidth / 2, y: window.innerHeight * .48 },
      outerRadius: Math.min(window.innerWidth, window.innerHeight) * .26,
    })
    state.stroke = [...fixture.points]
    state.totalStrokePoints = state.stroke.length
    state.gestureStage = 'drawing'
    state.strokeKind = 'whole'
    finalizeStroke()
    return state.sigilResult
  },
  reset: () => state.mode === 'camera' ? requireFive() : beginTouchFlow(),
}

debugPanel.addEventListener('click', () => window.__MAGIC__.simulateCast('triangle'))
updateTraceTools()
resizeCanvas()
if (debug) {
  buildVersion.hidden = false
  traceTools.hidden = false
  debugPanel.hidden = false
  debugPanel.title = '點擊可模擬一筆施法結果'
}
